// List of clevertap event for stockbit securities
// https://www.notion.so/stockbit/Stockbit-Buy-Sell-Order-Web-e129c61e0ae64d70abdb0d1811c5ec71
// Clevertap event is wrapped with high order function
// to separate concern with existing code.

import { ORDER_TYPE } from 'constants/trading';
import {
  ORDER_DETAIL_CANCEL_ORDER_SELECTED,
  ORDER_DETAIL_CANCEL_ORDER_VIEWED,
  ORDER_DETAIL_CANCEL_ORDER_CONFIRMED,
  ORDER_DETAIL_AMEND_SELECTED,
  AMEND_ORDER_VIEWED,
  AMEND_ORDER_QUANTITY_ADJUSTED,
  AMEND_ORDER_PLACED,
  AMEND_ORDER_PREVIEWED,
  AMEND_ORDER_CANCELED,
  AMEND_ORDER_CONFIRMED,
  AMEND_ORDER_RECEIVED,
  AMEND_ORDER_DONE,
  BUY_ORDER_QUANTITY_ADJUSTED,
  BUY_ORDER_PLACED,
  BUY_ORDER_VIEWED,
  BUY_ORDER_PREVIEWED,
  BUY_ORDER_CANCELED,
  BUY_ORDER_CONFIRMED,
  BUY_ORDER_RECEIVED,
  BUY_ORDER_DONE,
  BUY_ERROR_PAGE,
  SELL_ORDER_QUANTITY_ADJUSTED,
  SELL_ORDER_PLACED,
  SELL_ORDER_PREVIEWED,
  SELL_ORDER_VIEWED,
  SELL_ORDER_CANCELED,
  SELL_ORDER_CONFIRMED,
  SELL_ORDER_RECEIVED,
  SELL_ORDER_DONE,
  EXCHANGE_FEE_SELECTED,
  EXCHANGE_FEE_VIEWED,
  CHANGE_STOCK_VIEWED,
  PORTFOLIO_VIEWED,
  PORTFOLIO_ORDER_VIEWED,
  PORTFOLIO_SELL_STOCK_SELECTED,
  PORTFOLIO_COMPANY_SELL_STOCK_SELECTED,
  PORTFOLIO_STOCK_DETAIL_VIEWED,
  ORDER_VIEWED,
  ORDER_DETAIL_VIEWED,
  STOCK_SELECTED,
  PORTFOLIO_EXERCISE_CLICKED,
  PORTFOLIO_DETAIL_EXERCISE_SELECTED,
  EXERCISE_VIEWED,
  EXERCISE_SELECTED,
  EXERCISE_ERROR_PAGE,
  EXERCISE_ORDER_PREVIEWED,
  EXERCISE_ORDER_CONFIRMED,
  EXERCISE_ORDER_CANCELED,
  TRANSACTION_ORDER_FAILED,
  TRANSACTION_ORDER_FAILED_CONFIRMED,
  TRANSACTION_ORDER_ALERT,
  BUY_STOCK_CONTINUE,
  PORTFOLIO_BUY_MORE_SELECTED,
  PORTFOLIO_VIEW_CHART_SELECTED,
  PORTFOLIO_SET_PRICE_ALERT_SELECTED,
  DAY_TRADE_ACTION,
  AUTO_BUY_ACTION,
  DAY_TRADE_ACTIVATION_VIEWED,
  DAY_TRADE_TNC_VIEWED,
  DAY_TRADE_ACTIVATION_SUCCESS_VIEWED,
  DAY_TRADE_ACTIVATION_SELECTED,
  DAY_TRADE_ACTIVATION_DISMISSED,
  DAY_TRADE_ACTIVATION_SUBMITTED,
  DAY_TRADE_ACTIVATION_SUCCESS_SELECTED,
  DAY_TRADE_LEVERAGE_OPTION_VIEWED,
  DAY_TRADE_LEVERAGE_ACTIVATION_SELECTED,
  DAY_TRADE_ALERT_AFTER_MARKET_VIEWED,
  DAY_TRADE_ALERT_DEBT_RATIO_VIEWED,
  DAY_TRADE_ALERT_DEBT_RATIO_SUBMITTED,
  DAY_TRADE_DEBT_RATIO_CHIP_SELECTED,
  DAY_TRADE_DEBT_RATIO_MODAL_VIEWED,
  DAY_TRADE_STOCK_LIST_VIEWED,
  DAY_TRADE_STOCK_SORT_VIEWED,
  DAY_TRADE_LEVERAGE_SELECTED,
  DAY_TRADE_SORT_SELECTED,
  DAY_TRADE_SORT_APPLIED,
  DAY_TRADE_ORDER_TYPE_SELECTED,
  DAY_TRADE_SPLIT_ORDER_SELECTED,
  DAY_TRADE_ORDER_DETAIL_SELECTED,
  DAY_TRADE_SET_VISIBLE_BALANCE,
  SECURITIES_TRADING_PIN_VIEWED,
  SECURITIES_TRADING_PIN_SUBMITTED,
  SECURITIES_AUTO_ORDER_OPTIONS_VIEWED,
  SECURITIES_ORDER_TAB_SELECTED,
} from 'constants/clevertap';
import { clevertapEvent } from 'utils/clevertap';
import { capitalizeFirstLetter } from 'utils/general';
import { lotSize } from 'global/TradingModal/constant';
import {
  VIRTUAL_TRADING_TYPES,
  VIRTUAL_TRADING_SOURCE,
  PORTFOLIOS_TYPE,
} from './virtualTrading';

// Symbol type for clevertap is different from
// exisitng API response
export function generateSymbolType(symbol) {
  const [, symbolType] = symbol.split('-');
  if (!symbolType) {
    return 'Normal';
  }
  if (symbolType.toUpperCase()[0] === 'R') {
    return 'Right';
  }
  if (symbolType.toUpperCase()[0] === 'W') {
    return 'Warrant';
  }

  return '';
}

export function clevertapOrderDetailCancel(
  type = VIRTUAL_TRADING_TYPES.realTrading,
  symbol = '',
  orderType = 'Buy',
) {
  clevertapEvent(ORDER_DETAIL_CANCEL_ORDER_SELECTED, {
    Type: type,
    Symbol: symbol,
    'Symbol Type': generateSymbolType(symbol),
    'Order Type': capitalizeFirstLetter(orderType),
  });
  clevertapEvent(ORDER_DETAIL_CANCEL_ORDER_VIEWED);
}

export function clevertapOrderDetailCancelConfirmed({
  symbol,
  orderType,
  expiry,
  leverage,
}: {
  symbol: string;
  orderType: string;
  expiry: string;
  leverage: string;
}) {
  clevertapEvent(ORDER_DETAIL_CANCEL_ORDER_CONFIRMED, {
    symbol,
    'Order Type': capitalizeFirstLetter(orderType),
    'Symbol Type': generateSymbolType(symbol),
    Expiry: expiry,
    'Leverage Type': leverage,
  });
}

export function clevertapOrderDetailAmend({
  type = VIRTUAL_TRADING_TYPES.realTrading,
  symbol = '',
}) {
  clevertapEvent(ORDER_DETAIL_AMEND_SELECTED, {
    Type: type,
    symbol,
    'Symbol Type': generateSymbolType(symbol),
  });
}

export function clevertapOrderDetailAmendViewed({ symbol, orderType, type }) {
  clevertapEvent(AMEND_ORDER_VIEWED, {
    Symbol: symbol,
    'Order Type': capitalizeFirstLetter(orderType),
    'Symbol Type': generateSymbolType(symbol),
    Type: type,
  });
}

export function clevertapOrderQuantityAdjusted({
  symbol,
  orderType,
  adjustedType,
}) {
  const configParams = {
    [ORDER_TYPE.BUY]: {
      key: BUY_ORDER_QUANTITY_ADJUSTED,
      params: {
        Type: adjustedType,
      },
    },
    [ORDER_TYPE.SELL]: {
      key: SELL_ORDER_QUANTITY_ADJUSTED,
      params: {
        Type: adjustedType,
      },
    },
    [ORDER_TYPE.AMEND_BUY]: {
      key: AMEND_ORDER_QUANTITY_ADJUSTED,
      params: {
        Symbol: symbol,
        Type: adjustedType,
        'Order Type': capitalizeFirstLetter(ORDER_TYPE.BUY),
        'Symbol Type': generateSymbolType(symbol),
      },
    },
    [ORDER_TYPE.AMEND_SELL]: {
      key: AMEND_ORDER_QUANTITY_ADJUSTED,
      params: {
        Symbol: symbol,
        Type: adjustedType,
        'Order Type': capitalizeFirstLetter(ORDER_TYPE.SELL),
        'Symbol Type': generateSymbolType(symbol),
      },
    },
  };

  if (configParams[orderType] !== undefined) {
    clevertapEvent(configParams[orderType].key, configParams[orderType].params);
  }
}

export function clevertapOrderPlaced({
  orderType,
  symbol,
  price,
  lot,
  gainLoss,
  totalLot,
  tradingBalancePercentage,
  status,
  expiry,
  type,
  orderTypeStatus = 'Limit Order',
  splitOrder = false,
  dayTrade = false,
}: {
  orderType: any;
  symbol: any;
  price: number;
  gainLoss?: string;
  totalLot?: string;
  lot: number | string;
  tradingBalancePercentage?: string | number;
  status?: string;
  expiry: string;
  type: string;
  orderTypeStatus?: string;
  splitOrder?: boolean;
  dayTrade?: boolean;
}) {
  const configParams = {
    [ORDER_TYPE.BUY]: {
      key: BUY_ORDER_PLACED,
      params: {
        Symbol: symbol,
        Price: price,
        Lot: lot,
        '% Trading Balance': tradingBalancePercentage,
        Status: status,
        Expiry: expiry,
        Type: type,
        'Symbol Type': generateSymbolType(symbol),
        'Balance Lot': dayTrade ? 'Day Trade' : 'Regular',
        'Order Type': orderTypeStatus,
        'Split Order': splitOrder,
      },
    },
    [ORDER_TYPE.SELL]: {
      key: SELL_ORDER_PLACED,
      params: {
        Symbol: symbol,
        Price: price,
        Lot: lot,
        'Gain/Loss %': gainLoss,
        '% Total Lot': totalLot,
        Expiry: expiry,
        Type: type,
        'Symbol Type': generateSymbolType(symbol),
      },
    },
    [ORDER_TYPE.AMEND_BUY]: {
      key: AMEND_ORDER_PLACED,
      params: {
        Symbol: symbol,
        Price: price,
        Lot: lot,
        Status: status,
        Expiry: expiry,
        Type: type,
        'Order Type': capitalizeFirstLetter(ORDER_TYPE.BUY),
        'Leverage Type': dayTrade ? 'Day Trade' : 'Regular',
      },
    },
    [ORDER_TYPE.AMEND_SELL]: {
      key: AMEND_ORDER_PLACED,
      params: {
        Symbol: symbol,
        Price: price,
        Lot: lot,
        Status: status,
        Expiry: expiry,
        Type: type,
        'Order Type': capitalizeFirstLetter(ORDER_TYPE.SELL),
        'Leverage Type': dayTrade ? 'Day Trade' : 'Regular',
      },
    },
  };

  if (configParams[orderType] !== undefined) {
    clevertapEvent(configParams[orderType].key, configParams[orderType].params);
  }
}

export function clevertapOrderPreviewed({ symbol, orderType }) {
  const configParams = {
    [ORDER_TYPE.BUY]: {
      key: BUY_ORDER_PREVIEWED,
      params: {},
    },
    [ORDER_TYPE.SELL]: {
      key: SELL_ORDER_PREVIEWED,
      params: {},
    },
    [ORDER_TYPE.AMEND_BUY]: {
      key: AMEND_ORDER_PREVIEWED,
      params: {
        Symbol: symbol,
        'Order Type': capitalizeFirstLetter(ORDER_TYPE.BUY),
        'Symbol Type': generateSymbolType(symbol),
      },
    },
    [ORDER_TYPE.AMEND_SELL]: {
      key: AMEND_ORDER_PREVIEWED,
      params: {
        Symbol: symbol,
        'Order Type': capitalizeFirstLetter(ORDER_TYPE.SELL),
        'Symbol Type': generateSymbolType(symbol),
      },
    },
    [ORDER_TYPE.EXERCISE]: {
      key: EXERCISE_ORDER_PREVIEWED,
      params: {},
    },
  };

  if (configParams[orderType] !== undefined) {
    clevertapEvent(configParams[orderType].key, configParams[orderType].params);
  }
}

export function clevertapOrderCanceled({
  symbol,
  price,
  shares,
  orderType,
  type,
}) {
  const configParams = {
    [ORDER_TYPE.BUY]: {
      key: BUY_ORDER_CANCELED,
      params: {
        Symbol: symbol,
        Price: price,
        Lot: shares / lotSize,
        Type: type,
      },
    },
    [ORDER_TYPE.SELL]: {
      key: SELL_ORDER_CANCELED,
      params: {
        Symbol: symbol,
        Price: price,
        Lot: shares / lotSize,
      },
    },
    [ORDER_TYPE.AMEND_BUY]: {
      key: AMEND_ORDER_CANCELED,
      params: {
        Symbol: symbol,
        'Order Type': capitalizeFirstLetter(ORDER_TYPE.BUY),
        'Symbol Type': generateSymbolType(symbol),
      },
    },
    [ORDER_TYPE.AMEND_SELL]: {
      key: AMEND_ORDER_CANCELED,
      params: {
        Symbol: symbol,
        'Order Type': capitalizeFirstLetter(ORDER_TYPE.SELL),
        'Symbol Type': generateSymbolType(symbol),
      },
    },
    [ORDER_TYPE.EXERCISE]: {
      key: EXERCISE_ORDER_CANCELED,
      params: {
        Symbol: symbol,
        'Exercise Type': generateSymbolType(symbol),
        Price: price,
        Lot: shares / lotSize,
      },
    },
  };

  if (configParams[orderType] !== undefined) {
    clevertapEvent(configParams[orderType].key, configParams[orderType].params);
  }
}

export function clevertapOrderConfirmed({
  symbol,
  price,
  shares,
  orderType,
  type,
  expiry = 'Good for day',
  orderTypeStatus = 'Limit Order',
  splitOrder = false,
  dayTrade = false,
}) {
  const configParams = {
    [ORDER_TYPE.BUY]: {
      key: BUY_ORDER_CONFIRMED,
      params: {
        Symbol: symbol,
        Price: price,
        Lot: shares / lotSize,
        Expiry: expiry,
        'Symbol Type': generateSymbolType(symbol),
        'Balance Lot': dayTrade ? 'Day Trade' : 'Regular',
        'Order Type': orderTypeStatus,
        'Split Order': splitOrder,
      },
    },
    [ORDER_TYPE.SELL]: {
      key: SELL_ORDER_CONFIRMED,
      params: {
        Symbol: symbol,
        Price: price,
        Lot: shares / lotSize,
        Type: type,
        Expiry: expiry,
        'Balance Lot': dayTrade ? 'Day Trade' : 'Regular',
      },
    },
    [ORDER_TYPE.AMEND_BUY]: {
      key: AMEND_ORDER_CONFIRMED,
      params: {
        Symbol: symbol,
        'Order Type': capitalizeFirstLetter(ORDER_TYPE.BUY),
        'Symbol Type': generateSymbolType(symbol),
        Type: type,
      },
    },
    [ORDER_TYPE.AMEND_SELL]: {
      key: AMEND_ORDER_CONFIRMED,
      params: {
        Symbol: symbol,
        'Order Type': capitalizeFirstLetter(ORDER_TYPE.SELL),
        'Symbol Type': generateSymbolType(symbol),
        Type: type,
      },
    },
    [ORDER_TYPE.EXERCISE]: {
      key: EXERCISE_ORDER_CONFIRMED,
      params: {
        Symbol: symbol,
        'Exercise Type': generateSymbolType(symbol),
        Price: price,
        Lot: shares / lotSize,
      },
    },
  };

  if (configParams[orderType] !== undefined) {
    clevertapEvent(configParams[orderType].key, configParams[orderType].params);
  }
}

export function clevertapOrderReceived({ orderType }) {
  const configParams = {
    [ORDER_TYPE.BUY]: {
      key: BUY_ORDER_RECEIVED,
      params: {},
    },
    [ORDER_TYPE.SELL]: {
      key: SELL_ORDER_RECEIVED,
      params: {},
    },
    [ORDER_TYPE.AMEND_BUY]: {
      key: AMEND_ORDER_RECEIVED,
      params: {},
    },
    [ORDER_TYPE.AMEND_SELL]: {
      key: AMEND_ORDER_RECEIVED,
      params: {},
    },
  };

  if (configParams[orderType] !== undefined) {
    clevertapEvent(configParams[orderType].key, configParams[orderType].params);
  }
}

export function clevertapOrderDone({ orderType }) {
  const configParams = {
    [ORDER_TYPE.BUY]: {
      key: BUY_ORDER_DONE,
      params: {},
    },
    [ORDER_TYPE.SELL]: {
      key: SELL_ORDER_DONE,
      params: {},
    },
    [ORDER_TYPE.AMEND_BUY]: {
      key: AMEND_ORDER_DONE,
      params: {},
    },
    [ORDER_TYPE.AMEND_SELL]: {
      key: AMEND_ORDER_DONE,
      params: {},
    },
  };

  if (configParams[orderType] !== undefined) {
    clevertapEvent(configParams[orderType].key, configParams[orderType].params);
  }
}

export function clevertapExchangeFee(orderType) {
  clevertapEvent(EXCHANGE_FEE_SELECTED);
  clevertapEvent(EXCHANGE_FEE_VIEWED, {
    'Order Type': capitalizeFirstLetter(orderType),
  });
}

export function clevertapChangeStock() {
  clevertapEvent(CHANGE_STOCK_VIEWED);
}

export function clevertapBuyError() {
  clevertapEvent(BUY_ERROR_PAGE, {
    Reason: 'Insufficient Trading Limit',
  });
}

export function clevertapPortfolioSell({
  tradingType = VIRTUAL_TRADING_TYPES.realTrading,
  source,
  gainLoss,
  symbol,
}) {
  clevertapEvent(PORTFOLIO_SELL_STOCK_SELECTED, {
    Type: tradingType,
    Source: source,
    'Gain/Loss %': gainLoss,
    Symbol: symbol,
    'Symbol Type': generateSymbolType(symbol),
  });
}

export function clevertapPortfolioCompanySell(symbol: string) {
  clevertapEvent(PORTFOLIO_COMPANY_SELL_STOCK_SELECTED, {
    Symbol: symbol,
    'Symbol Type': generateSymbolType(symbol),
  });
}

export function clevertapPortfolioBuyMore(
  symbol,
  gainLoss,
  tradingType = VIRTUAL_TRADING_TYPES.realTrading,
) {
  clevertapEvent(PORTFOLIO_BUY_MORE_SELECTED, {
    Symbol: symbol,
    'Gain / Loss %': gainLoss,
    Type: tradingType,
  });
}

export function clevertapViewChartSelected(
  symbol,
  type = VIRTUAL_TRADING_TYPES.realTrading,
) {
  clevertapEvent(PORTFOLIO_VIEW_CHART_SELECTED, {
    Symbol: symbol,
    Type: type,
  });
}

export function clevertapSetPriceAlertSelected(
  symbol,
  type = VIRTUAL_TRADING_TYPES.realTrading,
) {
  clevertapEvent(PORTFOLIO_SET_PRICE_ALERT_SELECTED, {
    Symbol: symbol,
    Type: type,
  });
}

export function clevertapPortfolioViewed(
  tradingType = VIRTUAL_TRADING_TYPES.realTrading,
  portfolioType = PORTFOLIOS_TYPE.regular,
) {
  clevertapEvent(PORTFOLIO_VIEWED, {
    Type: tradingType,
    'Portfolio List': portfolioType,
  });
}

export function clevertapPortfolioOrderViewed(
  tradingType = VIRTUAL_TRADING_TYPES.realTrading,
  filter = 'Order',
) {
  clevertapEvent(PORTFOLIO_ORDER_VIEWED, {
    Type: tradingType,
    Filter: capitalizeFirstLetter(filter),
  });
}

export function clevertapOrderViewed() {
  clevertapEvent(ORDER_VIEWED);
}

export function clevertapOrderDetailViewed({
  orderType,
  status,
  rejectReason,
  expiry,
  symbol,
  type,
  dayTrade = false,
  dayTradeForceSell = false,
}) {
  clevertapEvent(ORDER_DETAIL_VIEWED, {
    'Order Type': capitalizeFirstLetter(orderType),
    Status: status,
    'Reject Reason': rejectReason,
    Expiry: expiry,
    Symbol: symbol,
    Type: type,
    'Symbol Type': generateSymbolType(symbol),
    'Leverage Type': dayTrade ? 'Day Trade' : 'Regular',
    'Force Sell Check': dayTradeForceSell ? 'Force Sell' : 'Regular',
  });
}

export function clevertapStockSelected() {
  clevertapEvent(STOCK_SELECTED);
}

export function clevertapExerciseClicked(symbol) {
  clevertapEvent(PORTFOLIO_EXERCISE_CLICKED, {
    Symbol: symbol,
  });
}

export function clevertapExerciseViewed({ symbol, share }) {
  return clevertapEvent(EXERCISE_VIEWED, {
    Symbol: symbol,
    'Exercise Type': generateSymbolType(symbol),
    Share: share,
  });
}

export function clevertapExerciseSelected({
  symbol,
  share,
  stockOnHandPercentage,
}) {
  clevertapEvent(EXERCISE_SELECTED, {
    Symbol: symbol,
    'Exercise Type': generateSymbolType(symbol),
    Share: share,
    '% Stock on Hand': stockOnHandPercentage,
  });
}

export function clevertapExerciseError() {
  clevertapEvent(EXERCISE_ERROR_PAGE, {
    Reason: 'Melebihi Cash on Hand',
  });
}

export function clevertapTransactionOrderFailed() {
  clevertapEvent(TRANSACTION_ORDER_FAILED);
}

export function clevertapTransactionOrderFailedConfirmed() {
  clevertapEvent(TRANSACTION_ORDER_FAILED_CONFIRMED);
}

export function clevertapTransactionOrderAlert() {
  clevertapEvent(TRANSACTION_ORDER_ALERT);
}

export function clevertapBuyStockContinue() {
  clevertapEvent(BUY_STOCK_CONTINUE);
}

export function clevertapPortfolioDetailViewed({
  symbol,
  gainLoss,
  exerciseable,
  dayTrade = false,
}) {
  clevertapEvent(PORTFOLIO_STOCK_DETAIL_VIEWED, {
    Symbol: symbol,
    'Symbol Type': generateSymbolType(symbol),
    'Gain/Loss %': gainLoss,
    Exercisable: exerciseable,
    'Leverage Type': dayTrade ? 'Day Trade' : 'Regular',
  });
}

export function clevertapPortfolioDetailExercise(symbol) {
  clevertapEvent(PORTFOLIO_DETAIL_EXERCISE_SELECTED, {
    Symbol: symbol,
    'Symbol Type': generateSymbolType(symbol),
  });
}

export function clevertapDayTradeActivationViewed() {
  clevertapEvent(DAY_TRADE_ACTIVATION_VIEWED);
}

export function clevertapDayTradeTNCViewed() {
  clevertapEvent(DAY_TRADE_TNC_VIEWED);
}

export function clevertapDayTradeActivationSuccessViewed() {
  clevertapEvent(DAY_TRADE_ACTIVATION_SUCCESS_VIEWED);
}

export function clevertapDayTradeActivationSelected() {
  clevertapEvent(DAY_TRADE_ACTION, {
    Action: DAY_TRADE_ACTIVATION_SELECTED,
  });
}

export function clevertapDayTradeActivationDismissed() {
  clevertapEvent(DAY_TRADE_ACTION, {
    Action: DAY_TRADE_ACTIVATION_DISMISSED,
  });
}

export function clevertapDayTradeActivationSubmitted() {
  clevertapEvent(DAY_TRADE_ACTION, {
    Action: DAY_TRADE_ACTIVATION_SUBMITTED,
  });
}

export function clevertapDayTradeActivationSuccessSelected() {
  clevertapEvent(DAY_TRADE_ACTION, {
    Action: DAY_TRADE_ACTIVATION_SUCCESS_SELECTED,
  });
}

export function clevertapDayTradeLeverageOptionViewed() {
  clevertapEvent(DAY_TRADE_LEVERAGE_OPTION_VIEWED);
}

export function clevertapDayTradeLeverageActivationSelected() {
  clevertapEvent(DAY_TRADE_ACTION, {
    Action: DAY_TRADE_LEVERAGE_ACTIVATION_SELECTED,
  });
}

export function clevertapDayTradeAfterMarketViewed() {
  clevertapEvent(DAY_TRADE_ALERT_AFTER_MARKET_VIEWED);
}

export function clevertapDayTradeDebtRatioViewed() {
  clevertapEvent(DAY_TRADE_ALERT_DEBT_RATIO_VIEWED);
}

export function clevertapDayTradeDebtRatioSubmitted() {
  clevertapEvent(DAY_TRADE_ACTION, {
    Action: DAY_TRADE_ALERT_DEBT_RATIO_SUBMITTED,
  });
}

export function clevertapDayTradeDebtRatioChipSelected() {
  clevertapEvent(DAY_TRADE_ACTION, {
    Action: DAY_TRADE_DEBT_RATIO_CHIP_SELECTED,
  });
}

export function clevertapDayTradeDebtRatioModalViewed() {
  clevertapEvent(DAY_TRADE_DEBT_RATIO_MODAL_VIEWED);
}

export function clevertapDayTradeStockListViewed({
  sourcePage = 'Company Page',
}) {
  clevertapEvent(DAY_TRADE_STOCK_LIST_VIEWED, {
    'Source Page': sourcePage,
  });
  clevertapEvent(DAY_TRADE_STOCK_SORT_VIEWED);
}

export function clevertapDayTradeLeverageSelected(leverage: string) {
  clevertapEvent(DAY_TRADE_LEVERAGE_SELECTED, {
    Action: DAY_TRADE_LEVERAGE_SELECTED,
    Leverage: leverage,
  });
}

export function clevertapDayTradeSortSelected() {
  clevertapEvent(DAY_TRADE_ACTION, {
    Action: DAY_TRADE_SORT_SELECTED,
  });
  clevertapEvent(DAY_TRADE_ACTION, {
    Action: DAY_TRADE_SORT_APPLIED,
  });
}

export function clevertapDayTradeOrderTypeSelected(
  stock: string,
  orderType: string,
) {
  clevertapEvent(AUTO_BUY_ACTION, {
    Action: DAY_TRADE_ORDER_TYPE_SELECTED,
    Stock: stock,
    'Buy Order Type': orderType,
  });
}

export function clevertapDayTradeSplitOrderSelected(toggle: boolean) {
  clevertapEvent(AUTO_BUY_ACTION, {
    Action: DAY_TRADE_SPLIT_ORDER_SELECTED,
    Toggle: toggle ? 'On' : 'Off',
  });
}

export function clevertapDayTradeOrderDetailSelected({
  symbol,
  dayTrade = false,
  forcedSell = false,
}) {
  clevertapEvent(DAY_TRADE_ORDER_DETAIL_SELECTED, {
    Symbol: symbol,
    'Leverage Type': dayTrade ? 'Day Trade' : 'Regular',
    'Force Sell Check': forcedSell ? 'Force Sell' : 'Regular',
  });
}

export function clevertapSecuritiesTradingPinViewed() {
  clevertapEvent(SECURITIES_TRADING_PIN_VIEWED);
}

export function clevertapSecuritiesTradingPinSubmitted() {
  clevertapEvent(SECURITIES_TRADING_PIN_SUBMITTED, {
    Method: 'PIN Input',
  });
}

export function clevertapBuyOrderViewed(symbol: string, sourceForm?: string) {
  const currentUrl = window.location.pathname.toLowerCase();
  let source = 'Other';

  if (!sourceForm) {
    if (currentUrl.includes('symbol')) {
      source = VIRTUAL_TRADING_SOURCE.companyPage;
    } else if (currentUrl.includes('stream') || currentUrl.includes('post')) {
      source = VIRTUAL_TRADING_SOURCE.predictionPost;
    } else if (currentUrl.includes('portfolio')) {
      source = VIRTUAL_TRADING_SOURCE.portfolio;
    } else if (currentUrl.includes('watchlist')) {
      source = VIRTUAL_TRADING_SOURCE.watchlist;
    }
  }

  clevertapEvent(BUY_ORDER_VIEWED, {
    Symbol: symbol,
    'Symbol Type': generateSymbolType(symbol),
    Source: source,
  });
}

export function clevertapSellOrderViewed(symbol: string) {
  clevertapEvent(SELL_ORDER_VIEWED, {
    Symbol: symbol,
    'Symbol Type': generateSymbolType(symbol),
  });
}

export function clevertapAutoOrderOptionsViewed() {
  clevertapEvent(SECURITIES_AUTO_ORDER_OPTIONS_VIEWED);
}

export function clevertapSetVisibleBalance() {
  clevertapEvent(DAY_TRADE_SET_VISIBLE_BALANCE);
}

export function clevertapOrderTabSelected(tab: string) {
  clevertapEvent('Order Detail Action', {
    Action: SECURITIES_ORDER_TAB_SELECTED,
    'Transaction Type': capitalizeFirstLetter(tab),
  });
}
